import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { makeGableRoofVerticalRails } from "../../roofTools/gableRoofExtrusion";

/**
 * Generates the vertical rails of a multi-gable roof. Those rails includes 2 rails (left/right)
 * for the start and end sides of the roof
 *
 *   /\-----------------/\
 *  /  \               /  \
 * /    \             /    \
 * 1     2            3     4
 *
 * 1 -> start left rail
 * 2 -> start right rail
 * 3 -> end left rail
 * 4 -> end right rail
 */
export function generateRoofVolumeVerticalRails({
  roofVolumeOptions,
  roofVolume,
  temporaries,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    legacyHeelHeightOffsets,
    heelHeightOffsets,
    baseHeight,
    baseToPeak,
    roofType,
    baseSlopeOptions,
    baseSlopes,
    leftSideSlopes,
    rightSideSlopes,
    rails2D,
    rectangularOverhangs,
    extensions,
    startTruncation,
    endTruncation,
    startConnectivity,
    endConnectivity,
  } = roofVolume;

  const { startBaseExtension, endBaseExtension } = temporaries;

  const { baseStartOverhang, baseEndOverhang, baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;

  const { startLeftExtensions, startRightExtensions, endLeftExtensions, endRightExtensions } = extensions;

  const { leftRail, rightRail, peakRail } = rails2D;

  const { roofInputSettings, neighborRoofs, insertedRoofVolumes, flipTruncationOrder } = roofVolumeOptions;

  const { wallSettings, roofSettings } = roofInputSettings;

  // build vertical rails
  const verticalRails = makeGableRoofVerticalRails({
    heelHeightOffsets,
    insertedRoofVolumes,
    leftRail,
    rightRail,
    peakRail,
    baseHeight,
    baseToPeak,
    leftSideSlopes,
    rightSideSlopes,
    baseSlopes,
    baseSlopeOptions,
    startLeftExtensions,
    startRightExtensions,
    endLeftExtensions,
    endRightExtensions,
    startTruncation,
    endTruncation,
    neighborRoofs,
    roofType,
    leftRoofOverhang: baseLeftOverhang,
    rightRoofOverhang: baseRightOverhang,
    startRoofOverhang: baseStartOverhang,
    endRoofOverhang: baseEndOverhang,
    isStartFree: startConnectivity.isFree,
    isEndFree: endConnectivity.isFree,
    roofSettings,
    wallSettings,
    startBaseExtension,
    endBaseExtension,
    flipTruncationOrder: !!flipTruncationOrder,
    legacyHeelHeightOffsets,
  });

  const { isStartFullyTruncated, isEndFullyTruncated } = verticalRails;

  return {
    roofVolume: {
      verticalRails,
      isStartFullyTruncated,
      isEndFullyTruncated,
    },
  };
}
